// src/store/modules/crm/mutations.js
import Vue from "vue";

// FT-154: страница догрузки доски; catch-up запрашивает кратное ей (≤ 200, потолок бэка).
export const BOARD_PAGE_SIZE = 50;

// Форма бакета стадии (REQ-10). hasMore по умолчанию true: бакет, созданный не из ответа
// сервера (MOVE_LEAD_OPTIMISTIC в пустую стадию), считается неполным — счётчики не гадаем.
export function makeBucket() {
  return { items: [], page: 1, total: 0, unreadCount: 0, hasMore: true, loadingMore: false, countsStale: false };
}

function removeFromStage(state, stageId, leadId) {
  const bucket = state.leadsByStage[stageId];
  if (!bucket) return null;
  const idx = bucket.items.findIndex((l) => l.id === leadId);
  if (idx === -1) return null;
  return bucket.items.splice(idx, 1)[0];
}

// --- Счётчики бакета при realtime (REQ-02): три случая по бакету, одна точка правды. ---
// (а) апдейт на месте — только дельта непрочитанности; (б) уход из бакета — −1 / −unread;
// (в) приход в бакет — +1 / +unread, если бакет полный; в неполном (лид мог быть за окном)
// счётчики помечаются stale и переписываются одним counts_only-запросом (refreshStageCounts).
// Бакеты старой формы (тесты, MOVE в несуществующую стадию) — поля берутся с дефолтами.
function countUpdate(b, prev, lead) {
  b.unreadCount = Math.max(0, (b.unreadCount || 0) + (lead.has_unread ? 1 : 0) - (prev.has_unread ? 1 : 0));
}
function countRemove(b, prev) {
  b.total = Math.max(0, (b.total || 0) - 1);
  if (prev.has_unread) b.unreadCount = Math.max(0, (b.unreadCount || 0) - 1);
}
function countInsert(b, lead) {
  if (b.hasMore) { b.countsStale = true; return; }
  b.total = (b.total || 0) + 1;
  if (lead.has_unread) b.unreadCount = (b.unreadCount || 0) + 1;
}
// Лид принадлежит текущему виду доски: архивный/живой вид (как было в UPSERT_LEAD) + серверные
// фильтры unread/important — realtime-upsert обязан уважать их так же, как fetchBoard.
function passesView(state, lead) {
  const showArchived = !!(state.filters && state.filters.archived);
  if (!!lead.archived_at !== showArchived) return false;
  if (state.unreadOnly && !lead.has_unread) return false;
  if (state.importantOnly && !lead.important) return false;
  return true;
}
function findLeadBucket(state, leadId) {
  for (const stageId of Object.keys(state.leadsByStage || {})) {
    const bucket = state.leadsByStage[stageId];
    const idx = bucket && bucket.items ? bucket.items.findIndex((l) => String(l.id) === String(leadId)) : -1;
    if (idx !== -1) return { stageId, bucket, idx, prev: bucket.items[idx] };
  }
  return null;
}

// Позиция по ключу сортировки доски = бэкендовый дефолт
// COALESCE(last_inbound_at, created_at) DESC, id DESC (leads_controller#sort_order)
function sortedInsertIndex(items, lead) {
  const sortKey = (l) => new Date(l.last_inbound_at || l.created_at).getTime() || 0;
  const k = sortKey(lead);
  const idx = items.findIndex((l) => {
    const lk = sortKey(l);
    return lk < k || (lk === k && Number(l.id) < Number(lead.id));
  });
  return idx === -1 ? items.length : idx;
}

// Открытый дровер должен видеть свежие данные лида независимо от того, из какого экрана
// он открыт. Раньше слияние жило внутри UPSERT_LEAD, который коммитит только канбан;
// инбокс коммитит UPSERT_INBOX_LEAD и selectedLead не трогал вовсе — поэтому item-контекст
// Avito (его дописывает EnrichChatJob уже ПОСЛЕ создания лида) появлялся только после
// переоткрытия карточки. Дровер же открывают в основном из инбокса.
//
// Плоские payload'ы (board-broadcast, экшены без include) несут только client_id/booking_id
// без объектов связей — не затираем уже загруженные объекты, пока id совпадает, иначе
// дровер показывал «Нет клиента» сразу после привязки брони.
function mergeIntoSelectedLead(state, lead) {
  if (!state.selectedLead || state.selectedLead.id !== lead.id) return;

  const prev = state.selectedLead;
  // FT-154 REQ-12: тонкий payload (доска, плоские ответы экшенов) не затирает дровер — скаляр,
  // отсутствующий в payload, остаётся из prev; явный null — значение, а не «отсутствует».
  const merged = { ...prev, ...lead };
  // metadata — jsonb-объект: тонкий объект несёт только канальный триплет, полный (show/detail) —
  // ещё Avito-контекст; сливаем по ключам, чтобы триплет не подменил контекст пустотой.
  if (lead.metadata && prev.metadata && typeof lead.metadata === "object" && typeof prev.metadata === "object") {
    merged.metadata = { ...prev.metadata, ...lead.metadata };
  }
  // Объект связи переносится из prev только при совпадении FK; смена FK без объекта — сброс
  // (иначе после перепривязки клиента дровер показывал бы прежнего).
  [ [ "client", "client_id" ], [ "booking", "booking_id" ], [ "pipeline_stage", "pipeline_stage_id" ], [ "lead_source", "lead_source_id" ] ]
    .forEach(([ rel, fk ]) => {
      if (lead[rel] !== undefined) return; // объект пришёл — он и есть истина
      const prevObj = prev[rel];
      if (!prevObj) return;
      const fkKnown = lead[fk] !== undefined;
      if (fkKnown && lead[fk] !== prevObj.id) delete merged[rel]; else merged[rel] = prevObj;
    });
  state.selectedLead = merged;
}

export default {
  SET_WEBSITE_CHAT(state, session) {
    if (!state.websiteChatSessions) Vue.set(state, "websiteChatSessions", {});
    const previous = state.websiteChatSessions[session.id];
    if (!session.document_requests && !(previous && previous.document_requests)) {
      Vue.set(state.websiteChatSessions, session.id, session);
      return;
    }
    const requests = Object.fromEntries(((previous && previous.document_requests) || []).map(request => [request.id, request]));
    (session.document_requests || []).forEach((request) => {
      const current = requests[request.id];
      if (!current || Number(request.revision || 0) >= Number(current.revision || 0)) requests[request.id] = request;
    });
    const olderOwnership = previous && Number(session.ownership_epoch || 0) < Number(previous.ownership_epoch || 0);
    const merged = olderOwnership ? { ...session, ...previous } : { ...previous, ...session };
    if (session.document_requests || (previous && previous.document_requests)) merged.document_requests = Object.values(requests);
    Vue.set(state.websiteChatSessions, session.id, merged);
  },
  SET_WEBSITE_CHAT_DOCUMENT_REQUEST(state, { request, sessionId }) {
    if (!request || !request.id) return;
    const sessions = state.websiteChatSessions || {};
    (sessionId && sessions[sessionId] ? [sessionId] : []).forEach((id) => {
      const session = sessions[id];
      const requests = session.document_requests || [];
      const index = requests.findIndex((item) => item.id === request.id);
      const current = index >= 0 ? requests[index] : null;
      if (current && Number(request.revision || 0) < Number(current.revision || 0)) return;
      const next = requests.slice();
      if (index >= 0) next.splice(index, 1, request); else next.push(request);
      Vue.set(sessions, id, { ...session, document_requests: next });
    });
  },
  SET_CHAT_WIDGETS(state, { widgets, ...directories }) {
    state.chatWidgets = widgets; state.chatWidgetDirectories = directories;
  },
  UPSERT_CHAT_WIDGET(state, widget) {
    const widgets = state.chatWidgets || [];
    state.chatWidgets = widgets.filter((item) => item.id !== widget.id).concat(widget).sort((a, b) => a.id - b.id);
  },

  SET_REFERENCE(state, { pipelines, leadSources, usersById }) {
    state.pipelines = pipelines || [];
    state.leadSources = leadSources || [];
    state.usersById = usersById || {};
    state.referenceLoaded = true;
    state.referenceLoading = false;
    state.referenceError = null;
  },
  SET_REFERENCE_LOADING(state, v) { state.referenceLoading = v; },
  SET_REFERENCE_ERROR(state, e) { state.referenceError = e; state.referenceLoading = false; },

  SET_VIEW_MODE(state, mode) { state.viewMode = mode; },
  SET_CURRENT_PIPELINE(state, id) { state.currentPipelineId = id; },
  // FT-156 REQ-07: клик по шапке колонки. Первый toggle в воронке материализует запись из
  // дефолта (терминальные стадии этой воронки), чтобы явно развёрнутая терминальная не
  // «сворачивалась обратно» дефолтом; битая запись (не массив) — тоже с дефолта.
  TOGGLE_STAGE_COLLAPSED(state, { pipelineId, stageId }) {
    // Массив из битого персиста тоже «не объект»: Vue.set по большому pipelineId сделал бы из него
    // гигантский разреженный массив, который персист сериализует целиком.
    if (!state.collapsedStages || typeof state.collapsedStages !== "object" || Array.isArray(state.collapsedStages)) state.collapsedStages = {};
    let record = state.collapsedStages[pipelineId];
    if (!Array.isArray(record)) {
      const p = (state.pipelines || []).find((x) => x.id === pipelineId);
      record = ((p && p.pipeline_stages) || [])
        .filter((s) => s.stage_type === "won" || s.stage_type === "lost")
        .map((s) => s.id);
    }
    const next = record.includes(stageId) ? record.filter((id) => id !== stageId) : [...record, stageId];
    Vue.set(state.collapsedStages, pipelineId, next);
  },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_UNREAD_ONLY(state, v) { state.unreadOnly = !!v; },
  SET_IMPORTANT_ONLY(state, v) { state.importantOnly = !!v; },
  RESET_FILTERS(state) {
    state.filters = { q: "", source_id: null, assigned_to_id: null, pipeline_stage_id: null, archived: false };
    state.unreadOnly = false;    // FE-флаги тоже сбрасываем, иначе остаются скрыто активными
    state.importantOnly = false; // в list/inbox-видах (тумблеры скрыты) после «Сбросить всё»
  },

  SET_LIST(state, { items, meta }) { state.leadsList.items = items; state.leadsList.meta = meta; },
  SET_LIST_LOADING(state, v) { state.leadsList.loading = v; },
  SET_LIST_ERROR(state, e) { state.leadsList.error = e; },
  SET_INBOX(state, { items, meta }) { state.inbox.items = items; state.inbox.meta = meta; },
  APPEND_INBOX(state, { items, meta }) { state.inbox.items = state.inbox.items.concat(items); state.inbox.meta = meta; },
  SET_INBOX_LOADING(state, v) { state.inbox.loading = v; },
  SET_INBOX_ERROR(state, e) { state.inbox.error = e; },
  REMOVE_INBOX_LEAD(state, leadId) { state.inbox.items = state.inbox.items.filter((l) => l.id !== leadId); },
  // Live-вставка/обновление лида в инбоксе по cable-событию доски. Сортировка по
  // last_inbound_at DESC (как в серверном инбоксе). board-payload без last_message —
  // при обновлении существующего сохраняем уже загруженный сниппет.
  UPSERT_INBOX_LEAD(state, lead) {
    const items = state.inbox.items;
    const idx = items.findIndex((l) => l.id === lead.id);
    let merged = lead;
    if (idx !== -1) {
      merged = { ...items[idx], ...lead };
      if (lead.last_message == null && items[idx].last_message) merged.last_message = items[idx].last_message;
      items.splice(idx, 1);
    }
    const ts = (l) => new Date(l.last_inbound_at || l.created_at || 0).getTime();
    let pos = items.findIndex((l) => ts(l) < ts(merged));
    if (pos === -1) pos = items.length;
    items.splice(pos, 0, merged);
    // Тот же лид может быть открыт в дровере — обновляем и его (см. mergeIntoSelectedLead).
    mergeIntoSelectedLead(state, merged);
  },

  // Первая страница стадии (или catch-up на глубину). page — от размера ответа: catch-up
  // приходит одним запросом на N страниц; пустая стадия → page 1, не 0.
  SET_STAGE_LEADS(state, { stageId, items, meta }) {
    const list = items || [];
    const m = meta || {};
    Vue.set(state.leadsByStage, stageId, {
      ...makeBucket(),
      items: list,
      page: Math.max(1, Math.ceil(list.length / BOARD_PAGE_SIZE)),
      total: m.total_count || 0,
      unreadCount: m.unread_count || 0,
      hasMore: !!m.has_more,
    });
  },
  // Догрузка следующей страницы: дедуп по id (offset-дрейф при live-обновлениях — NS-07),
  // счётчики из meta (серверные авторитетнее локальных дельт). Пустая страница — стоп,
  // иначе observer на sentinel зациклился бы на «есть ещё».
  APPEND_STAGE_LEADS(state, { stageId, items, meta }) {
    const b = state.leadsByStage[stageId] || Vue.set(state.leadsByStage, stageId, makeBucket());
    const ids = new Set(b.items.map((l) => l.id));
    const incoming = (items || []).filter((l) => !ids.has(l.id));
    b.items.push(...incoming);
    const m = meta || {};
    if (m.page) b.page = m.page;
    if (m.total_count != null) b.total = m.total_count;
    if (m.unread_count != null) b.unreadCount = m.unread_count;
    b.hasMore = !!m.has_more && (items || []).length > 0;
    b.countsStale = false;
  },
  // counts_only-ответ: только счётчики. hasMore считаем сами от загруженного — этот запрос
  // идёт без per_page и серверный has_more в нём посчитан от совместимого дефолта 200.
  SET_STAGE_COUNTS(state, stages) {
    Object.keys(stages || {}).forEach((stageId) => {
      const b = state.leadsByStage[stageId];
      const m = stages[stageId] && stages[stageId].meta;
      if (!b || !m) return;
      if (m.total_count != null) b.total = m.total_count;
      if (m.unread_count != null) b.unreadCount = m.unread_count;
      b.hasMore = b.items.length < b.total;
      b.countsStale = false;
    });
  },
  SET_STAGE_LOADING_MORE(state, { stageId, value }) {
    const b = state.leadsByStage[stageId];
    if (b) b.loadingMore = !!value;
  },
  SET_KANBAN_LOADING(state, v) { state.kanbanLoading = v; },
  BUMP_BOARD_GENERATION(state) { state.boardGeneration += 1; },
  SET_KANBAN_ERROR(state, e) { state.kanbanError = e; },
  SET_CATCHING_UP(state, v) { state.catchingUp = !!v; },
  SET_CATCH_UP_PENDING(state, v) { state.catchUpPending = !!v; },

  // Optimistic-перенос: источник платит (б) сразу, цель — только после подтверждения PATCH
  // (CLEAR_PENDING_MOVE), иначе counts_only мог бы откатить числа до коммита на сервере.
  MOVE_LEAD_OPTIMISTIC(state, { lead_id, from, to }) {
    const source = state.leadsByStage[from];
    const lead = removeFromStage(state, from, lead_id);
    if (!lead) return;
    if (source) countRemove(source, lead);
    lead.pipeline_stage_id = to;
    if (!state.leadsByStage[to]) Vue.set(state.leadsByStage, to, makeBucket());
    // Сразу финальная позиция (как UPSERT_LEAD по ответу сервера) — без двойного прыжка карточки
    const items = state.leadsByStage[to].items;
    items.splice(sortedInsertIndex(items, lead), 0, lead);
  },
  // Откат неудавшегося переноса: цель — без countRemove (её счётчики не трогались), источник —
  // вернуть лид и ровно то, что снял optimistic. Обратный MOVE_LEAD_OPTIMISTIC для этого не годится.
  ROLLBACK_PENDING_MOVE(state, { lead_id, from, to }) {
    const lead = removeFromStage(state, to, lead_id);
    if (!lead) return;
    lead.pipeline_stage_id = from;
    if (!state.leadsByStage[from]) Vue.set(state.leadsByStage, from, makeBucket());
    const source = state.leadsByStage[from];
    source.items.splice(sortedInsertIndex(source.items, lead), 0, lead);
    source.total = (source.total || 0) + 1;
    if (lead.has_unread) source.unreadCount = (source.unreadCount || 0) + 1;
  },
  UPSERT_LEAD(state, lead) {
    // prev — до вырезания копий: по нему считаются дельты (а)/(б).
    const found = findLeadBucket(state, lead.id);
    const target = state.leadsByStage[lead.pipeline_stage_id];
    const passes = passesView(state, lead);

    if (found && found.bucket === target && passes) {
      // (а) апдейт на месте: вставка по ключу сортировки доски, НЕ unshift — иначе любой апдейт
      // (прочтение, смена ответственного) прыгал бы карточкой наверх. Свежее входящее
      // обновляет ключ → поднимается; прочие правки — на месте.
      countUpdate(target, found.prev, lead);
      target.items.splice(found.idx, 1);
      target.items.splice(sortedInsertIndex(target.items, lead), 0, lead);
    } else {
      // (б) уход из прежнего бакета: перенос в другую стадию, архивация, выпадение из фильтра.
      if (found) { found.bucket.items.splice(found.idx, 1); countRemove(found.bucket, found.prev); }
      if (target && passes) {
        // (в) приход в бакет, где лида не было
        target.items.splice(sortedInsertIndex(target.items, lead), 0, lead);
        countInsert(target, lead);
      } else if (target && !found && target.hasMore) {
        // Лид чужого вида, которого в окне нет: в неполном бакете он мог быть за окном
        // (прочитан/разархивирован за окном) — счётчики стадии больше не достоверны.
        target.countsStale = true;
      }
    }
    mergeIntoSelectedLead(state, lead);
  },
  REMOVE_LEAD(state, leadId) {
    const found = findLeadBucket(state, leadId);
    if (!found) return;
    found.bucket.items.splice(found.idx, 1);
    countRemove(found.bucket, found.prev);
  },
  // Идемпотентно по фактическому has_unread: дровер зовёт markLeadRead при каждом открытии,
  // а после optimistic-прочтения прилетает broadcast с тем же значением — оба дают 0, не −1.
  UPSERT_LEAD_UNREAD(state, { leadId, value }) {
    const found = findLeadBucket(state, leadId);
    if (!found) return;
    if (!!found.prev.has_unread === !!value) return;
    countUpdate(found.bucket, found.prev, { has_unread: value });
    Vue.set(found.prev, "has_unread", value); // Vue 2: use Vue.set for potentially absent properties
  },
  SET_PENDING_MOVE(state, { lead_id, from, to }) {
    Vue.set(state.pendingMoves, lead_id, { from_stage_id: from, to_stage_id: to });
  },
  // Подтверждение переноса сервером: (в) для целевой стадии — единственное место, где цель
  // получает +1 (ответ advanceLead уже прошёл через UPSERT_LEAD как случай (а) → 0).
  CLEAR_PENDING_MOVE(state, lead_id) {
    const pending = state.pendingMoves[lead_id];
    if (pending) {
      const target = state.leadsByStage[pending.to_stage_id];
      const l = target && target.items.find((x) => String(x.id) === String(lead_id));
      if (target && l) countInsert(target, l);
    }
    Vue.delete(state.pendingMoves, lead_id);
  },

  SET_SELECTED_LEAD(state, lead) { state.selectedLead = lead; state.selectedLeadId = lead ? lead.id : null; },
  SET_SELECTED_LOADING(state, v) { state.selectedLeadLoading = v; },
  SET_SELECTED_ERROR(state, e) { state.selectedLeadError = e; },

  // Мульти-сессии: upsert по leadId (повторный вход обновляет имя/клиента, draft сохраняется).
  // Кап 5 параллельных конвертаций — при превышении молча вытесняется самая старая.
  START_CONVERSION(state, { leadId, leadName, clientId }) {
    const existing = state.conversions.find((c) => String(c.leadId) === String(leadId));
    if (existing) {
      existing.leadName = leadName || existing.leadName;
      existing.clientId = clientId || existing.clientId;
      return;
    }
    state.conversions.push({ leadId, leadName: leadName || null, clientId: clientId || null, draft: null });
    if (state.conversions.length > 5) state.conversions.shift();
  },
  // Черновик (даты/машина) мержится: даты пишутся watcher'ом, машина — на pickCar.
  SET_CONVERSION_DRAFT(state, { leadId, draft }) {
    const session = state.conversions.find((c) => String(c.leadId) === String(leadId));
    if (session) session.draft = { ...(session.draft || {}), ...draft };
  },
  CLEAR_CONVERSION(state, leadId) {
    state.conversions = state.conversions.filter((c) => String(c.leadId) !== String(leadId));
  },
  // FT-059 pipeline editor
  UPSERT_PIPELINE(state, pipeline) {
    // Mirror the backend's exactly-one-default invariant: the server demoted the
    // previous default, but the response only carries this (newly-promoted) pipeline,
    // so demote the others locally to avoid a stale "default" badge on two pipelines.
    if (pipeline.is_default) {
      state.pipelines.forEach((p) => { if (p.id !== pipeline.id && p.is_default) p.is_default = false; });
    }
    const idx = state.pipelines.findIndex((p) => p.id === pipeline.id);
    if (idx !== -1) Vue.set(state.pipelines, idx, pipeline);
    else state.pipelines.push(pipeline);
  },
  REMOVE_PIPELINE(state, id) {
    state.pipelines = state.pipelines.filter((p) => p.id !== id);
    if (state.currentPipelineId === id) {
      const def = state.pipelines.find((p) => p.is_default) || state.pipelines[0] || null;
      state.currentPipelineId = def ? def.id : null;
    }
  },
  // FT-062 lead-sources editor — write-endpoints return the full branch list
  SET_LEAD_SOURCES(state, sources) { state.leadSources = sources || []; },

  // Inbox (045b)
  SET_LEAD_MESSAGES_LOADING(state, v) { state.leadMessages.loading = v; },
  SET_LEAD_MESSAGES_ERROR(state, e) { state.leadMessages.error = e; },
  SET_LEAD_MESSAGES(state, { items, identity, hasMore }) {
    state.leadMessages = { items, identity, hasMore, loading: false, error: null };
  },
  PREPEND_LEAD_MESSAGES(state, { items, hasMore }) {
    state.leadMessages.items = items.concat(state.leadMessages.items);
    state.leadMessages.hasMore = hasMore;
  },
  APPEND_LEAD_MESSAGE(state, message) {
    const shown = state.leadMessages.items.find((m) => m.id === message.id);
    if (!shown) { state.leadMessages.items.push(message); return; }
    // Повтор того же сообщения — без дубля (echo-safe). FT-177: эхо вебхука могло показать ответ
    // раньше, чем путь записи дописал автора; повтор с автором дописывает его в показанное
    // сообщение. Только непустые значения — поздний повтор без автора подпись не стирает.
    // Vue 2: ключей автора на объекте из ленты или эха может не быть — Vue.set.
    ["user_id", "author_name"].forEach((key) => {
      if (message[key] !== null && message[key] !== undefined && message[key] !== "") Vue.set(shown, key, message[key]);
    });
  },
  // Статус доставки исходящего (sent/delivered/read/failed) пришёл вебхуком — обновляем in place.
  UPDATE_LEAD_MESSAGE_STATUS(state, { id, status }) {
    const m = state.leadMessages.items.find((x) => x.id === id);
    if (m) Vue.set(m, "status", status); // Vue 2: status может отсутствовать на объекте из broadcast
  },
  SET_REPLY_SENDING(state, v) { state.replySending = v; },
  // FT-171: флаг ассистента приходит с leads#detail; latest — по лиду (гард leadId в экшене).
  SET_ASSISTANT_FLAG(state, flag) { state.assistant = { ...state.assistant, flag: flag || null }; },
  SET_ASSISTANT_LATEST(state, { leadId, latest }) { state.assistant = { ...state.assistant, leadId, latest: latest || null }; },
  RESET_ASSISTANT_LATEST(state) { state.assistant = { ...state.assistant, leadId: null, latest: null }; },
  RESET_LEAD_MESSAGES(state) {
    state.leadMessages = { items: [], identity: null, hasMore: false, loading: false, error: null };
  },

  // Activities (046a)
  SET_LEAD_ACTIVITIES_LOADING(state, v) { state.leadActivities.loading = v; },
  SET_LEAD_ACTIVITIES_ERROR(state, e) { state.leadActivities.error = e; },
  SET_LEAD_ACTIVITIES(state, { items, meta }) {
    state.leadActivities = { items, meta, loading: false, error: null, creating: false };
  },
  APPEND_LEAD_ACTIVITIES(state, { items, meta }) {
    state.leadActivities.items = state.leadActivities.items.concat(items);
    state.leadActivities.meta = meta;
  },
  PREPEND_LEAD_ACTIVITY(state, activity) { state.leadActivities.items.unshift(activity); },
  REMOVE_LEAD_ACTIVITY(state, id) {
    state.leadActivities.items = state.leadActivities.items.filter((a) => a.id !== id);
  },
  SET_ACTIVITIES_CREATING(state, v) { state.leadActivities.creating = v; },
  RESET_LEAD_ACTIVITIES(state) {
    state.leadActivities = { items: [], meta: null, loading: false, error: null, creating: false };
  },

  // Tasks (046b)
  SET_LEAD_TASKS_LOADING(state, v) { state.leadTasks.loading = v; },
  SET_LEAD_TASKS_ERROR(state, e) { state.leadTasks.error = e; },
  SET_LEAD_TASKS(state, { items, meta }) { state.leadTasks = { items, meta, loading: false, error: null, creating: false }; },
  APPEND_LEAD_TASKS(state, { items, meta }) { state.leadTasks.items = state.leadTasks.items.concat(items); state.leadTasks.meta = meta; },
  PREPEND_LEAD_TASK(state, task) { state.leadTasks.items.unshift(task); },
  SET_LEAD_TASKS_CREATING(state, v) { state.leadTasks.creating = v; },
  REMOVE_LEAD_TASK(state, id) { state.leadTasks.items = state.leadTasks.items.filter((t) => t.id !== id); },
  RESET_LEAD_TASKS(state) { state.leadTasks = { items: [], meta: null, loading: false, error: null, creating: false }; },
  UPSERT_TASK(state, task) {
    {
      const arr = state.leadTasks.items;
      const i = arr.findIndex((t) => t.id === task.id);
      if (i !== -1) arr.splice(i, 1, task);
      else arr.unshift(task);
    }
    {
      const arr = state.myTasks.items;
      const i = arr.findIndex((t) => t.id === task.id);
      if (i !== -1) arr.splice(i, 1, task);
    }
  },
  UPSERT_LEAD_ACTIVITY(state, activity) {
    const arr = state.leadActivities.items;
    const i = arr.findIndex((a) => a.id === activity.id);
    if (i !== -1) Vue.set(arr, i, activity);
    else arr.unshift(activity);
  },
  SET_MY_TASKS_LOADING(state, v) { state.myTasks.loading = v; },
  SET_MY_TASKS_ERROR(state, e) { state.myTasks.error = e; },
  SET_MY_TASKS(state, { items, meta }) { state.myTasks = { items, meta, loading: false, error: null }; },
  APPEND_MY_TASKS(state, { items, meta }) { state.myTasks.items = state.myTasks.items.concat(items); state.myTasks.meta = meta; },
  SET_ASSIGNABLE_USERS(state, users) { state.assignableUsers = users; },
};
